// SPDX-License-Identifier: Apache-2.0

// App shell — 3-zone grid: navbar (top) / route content (left) / chat side rail (right).
// The lens-tabs sit in the navbar row and span both columns above. The chat panel is the
// right rail, always mounted, so the brain agent is one keystroke away on every route.
import type { ParentProps } from "solid-js";
import { A, useLocation } from "@solidjs/router";
import { Show, onMount } from "solid-js";
import ChatPanel from "./components/ChatPanel";
import SystemHealthBanner from "./components/SystemHealthBanner";
import { startWorkspaceRegistrySync } from "./lib/workspace-sync";

// `/` is the 3D landing atlas (hero); `/vault` is the live product surface
// (orbit + ingest + search). The conversation lives in the right-rail
// ChatPanel — present on every non-/dev, non-/ route — so we don't need a
// separate "chat" topbar entry. The topbar lists *surfaces*; the side rail
// is where you talk to the brain regardless of which surface you're on.
const TABS = [
  { path: "/vault", label: "vault" },
  { path: "/sessions", label: "sessions" },
  { path: "/history", label: "history" },
  { path: "/temple", label: "temple" },
  { path: "/share", label: "share" },
  { path: "/journey", label: "journey" },
] as const;

// Routes where we don't want the chat side rail. Landing (`/`) is a full-bleed
// 3D hero; kernel diagnostics need full width; the temple has its own embedded
// harness; share is a public read-only renderer; journey is a cinematic
// walkthrough that takes the full viewport.
const NO_CHAT_PREFIXES = ["/dev", "/temple", "/share", "/journey"] as const;
// Exact-match routes where we hide the chat rail (so `/vault` keeps it but
// `/` doesn't, even though `/` is a strict prefix of `/vault`).
const NO_CHAT_EXACT = ["/"] as const;

// Routes that paint full-bleed (no top lens-tabs, no chat rail). Currently
// the landing atlas (its own footer covers the in-app nav) and the journey
// walkthrough (cinematic, no chrome).
const FULL_BLEED_EXACT = ["/", "/journey"] as const;

export default function App(props: ParentProps) {
  const loc = useLocation();
  const showChat = () =>
    !NO_CHAT_PREFIXES.some((p) => loc.pathname.startsWith(p)) &&
    !NO_CHAT_EXACT.includes(loc.pathname as (typeof NO_CHAT_EXACT)[number]);
  const fullBleed = () =>
    FULL_BLEED_EXACT.includes(loc.pathname as (typeof FULL_BLEED_EXACT)[number]);

  // Mirror the SPA's workspace registry to the local MCP server so that
  // `X-Workspace-Id` headers (sent by `lib/api.ts`) resolve to the right
  // vaultPath on every request.
  onMount(() => startWorkspaceRegistrySync());

  return (
    <div
      class="app-shell brain-shell"
      classList={{
        "brain-shell--no-chat": !showChat(),
        "brain-shell--full-bleed": fullBleed(),
      }}
    >
      <Show when={!fullBleed()}>
        <nav class="lens-tabs">
          {TABS.map((t) => (
            <A href={t.path} activeClass="active" end={t.path === "/vault"}>
              {t.label}
            </A>
          ))}
          <A class="settings-cog" href="/settings" activeClass="active" aria-label="settings">
            ⚙
          </A>
        </nav>
      </Show>
      <main class="brain-main">{props.children}</main>
      <Show when={showChat()}>
        <ChatPanel />
      </Show>
      {/* Discreet /dev tools link — fixed lower-right of the screen, low
          opacity until hover. Not loud, just discoverable. */}
      <A class="dev-footer-link" href="/dev">/dev</A>
    </div>
  );
}
